import React, { useMemo, useEffect, createContext, useState } from "react";

import { useHistory, useLocation } from "react-router-dom";
import { IconButton, Grid, GlobalStyles, Fab } from "@mui/material";
import { ChevronLeft, ChevronRight, Navigation } from "@mui/icons-material";
import { makeStyles } from "tss-react/mui";

import { RETRO_ORDER } from "./RetrospectiveData";
import { fetchRetrospective } from "api/retrospective";
import { useUserContext } from "contexts/UserContext";

const CONTEXT_DEFAULT: Record<
  string,
  null | number | string | Array<Record<string, number>>
> = {
  salasIluminadas: null,
  salasClimatizadas: null,
  consumoMedioFamiliar: null,
  co2EvitadoTotal: null,
  monthlyCO2: [],
  totalConsumoSomatorio: null,
  percentualEnergiaVerde: null,
  id: null,
  nextRoute: "01",
};

export const RetroContext = createContext(CONTEXT_DEFAULT);

const useStyles = makeStyles()((theme) => ({
  genericWrapper: {
    position: "relative",
    gridColumn: "span 1",
    gridRow: "1 / span 3",
    padding: theme.spacing(4, 5),

    "@media (orientation: landscape)": {
      paddingRight: "calc((100vw - 100vh)/2)",
      paddingLeft: "calc((100vw - 100vh)/2)",
    },
  },
  buttonLeft: {
    position: "absolute",
    color: "common.white",
    backgroundColor: "rgba(255, 255, 255, 0.4)",
    height: "48px",
    width: "48px",
    top: "calc(50% - 24px)",

    // vertical
    left: "calc(50% - min(50vw, 300px) + 24px)",

    // horizontal
    "@media (orientation: landscape)": {
      left: "calc((100vw - 100vh)/4)",
    },
  },
  buttonRight: {
    position: "absolute",
    color: "common.white",
    backgroundColor: "rgba(255, 255, 255, 0.4)",
    height: "48px",
    width: "48px",
    top: "calc(50% - 24px)",

    // vertical
    right: "calc(50% + 24px - min(300px, 50%))",
    // horizontal
    "@media (orientation: landscape)": {
      left: "calc(100% - (100vw - 100vh)/4)",
    },
  },
  buttonReturn: {
    position: "absolute",
    bottom: "20px",
    left: "20px",
  },
}));

export const RetrospectiveMain = ({ children }) => {
  const { classes } = useStyles();
  const { token } = useUserContext();
  const location = useLocation();
  const history = useHistory();
  const query = useMemo((): string => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.has("escola")) {
      return searchParams.get("escola") || "";
    } else {
      return "";
    }
  }, [location]);
  const [retroData, setRetroData] =
    useState<typeof CONTEXT_DEFAULT>(CONTEXT_DEFAULT);

  const [prevRoute, nextRoute] = useMemo(() => {
    const { pathname } = location;

    const currentIndex = RETRO_ORDER.findIndex(
      (elmt) => elmt.valueOf() === pathname
    );
    const prevRoute = RETRO_ORDER[currentIndex - 1];
    const nextRoute = RETRO_ORDER[currentIndex + 1];
    return [prevRoute, nextRoute, currentIndex];
  }, [location]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await fetchRetrospective(query);
        setRetroData((prevData) => ({ ...prevData, ...data, id: query }));
      } catch (error) {}
    };

    if (query) {
      fetchData();
    }
  }, [query]);

  const handleNext = () => {
    history.push(`${nextRoute}`);
  };
  const handlePrev = () => {
    history.push(`${prevRoute}`);
  };

  return (
    <RetroContext.Provider value={retroData}>
      <Grid item container component="main" className={classes.genericWrapper}>
        <GlobalStyles
          styles={{
            "#root": { maxWidth: "none" },
          }}
        />
        {children}
        {!!prevRoute && (
          <IconButton
            size="large"
            className={classes.buttonLeft}
            onClick={handlePrev}
          >
            <ChevronLeft />
          </IconButton>
        )}

        {!!nextRoute && (
          <IconButton
            size="large"
            className={classes.buttonRight}
            onClick={handleNext}
          >
            <ChevronRight />
          </IconButton>
        )}

        {(!nextRoute || !prevRoute) && token && (
          <Fab
            variant="extended"
            sx={{ textTransform: "none" }}
            className={classes.buttonReturn}
            onClick={() => {
              history.push("/admin/decarbonization");
            }}
          >
            <Navigation sx={{ mr: 1 }} />
            Voltar ao app
          </Fab>
        )}
      </Grid>
    </RetroContext.Provider>
  );
};
